import React, { useEffect, useState, useRef } from 'react';
import bgImage from '../assets/bg.jpg';

const HeroSection = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const heroRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  const containerStyle = {
    backgroundImage: isMobile
      ? `linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 50%, rgba(0,0,0,0.5) 100%), url(${bgImage})`
      : `linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 35%, rgba(0,0,0,0.0) 65%), url(${bgImage})`,
    backgroundSize: 'cover',
    backgroundPosition: isMobile ? 'center center' : 'left center',
    minHeight: '700px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: isMobile ? '60px 20px' : '100px 50px',
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
  };

  const contentStyle = {
    maxWidth: '600px',
    width: '100%',
    textAlign: isMobile ? 'center' : 'left',
    borderRadius: '16px',
    padding: isMobile ? '0px' : '40px',
    zIndex: 2,
    position: 'relative',
    margin: '0',
    alignSelf: isMobile ? 'center' : 'flex-start',
  };

  const subHeadingStyle = {
    color: '#D6A54F',
    fontSize: isMobile ? '22px' : '28px',
    marginBottom: '15px',
    fontWeight: '600',
    letterSpacing: '1px',
    transform: 'translateY(20px)',
    opacity: 0,
  };

  const headingStyle = {
    fontSize: isMobile ? '35px' : '48px',
    margin: 0,
    lineHeight: '1.2',
    color: '#fff',
    fontFamily: 'Playfair Display',
    transform: 'translateY(20px)',
    opacity: 0,
    fontWeight: '500',
  };

  const paragraphStyle = {
    marginTop: '20px',
    fontSize: isMobile ? '14px' : '16px',
    color: 'white',
    lineHeight: '1.6',
    transform: 'translateY(20px)',
    opacity: 0,
    margin: '20px 0 0 0',
  };

  const buttonStyleBase = {
    margin: isMobile ? '30px auto 0' : '30px 0 0 0',
    padding: isMobile ? '10px 20px' : '12px 24px',
    backgroundColor: '#D6A54F',
    color: '#fff',
    border: 'none',
    borderRadius: '25px',
    fontSize: isMobile ? '13px' : '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    textDecoration: 'none',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1,
    transform: 'translateY(20px)',
    opacity: 0,
  };

  const getAnimatedStyle = (baseStyle, delay) => ({
    ...baseStyle,
    transform: isVisible ? 'translateY(0)' : baseStyle.transform,
    opacity: isVisible ? 1 : baseStyle.opacity,
    transition: `transform 1s ease ${delay}s, opacity 1s ease ${delay}s`,
  });

  const animatedSubHeadingStyle = getAnimatedStyle(subHeadingStyle, 0.2);
  const animatedHeadingStyle = getAnimatedStyle(headingStyle, 0.4);
  const animatedParagraphStyle = getAnimatedStyle(paragraphStyle, 0.6);
  const animatedButtonStyle = getAnimatedStyle(buttonStyleBase, 0.8);

  return (
    <>
      <style>
        {`
          .hero-button {
            transition: color 0.3s ease, background-color 0.3s ease;
            position: relative;
            overflow: hidden;
            z-index: 1;
          }

          .hero-button::before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: -15%;
            width: 0;
            height: 106%;
            background-color: #C08E3B;
            transform: skew(-25deg);
            transition: width 0.4s ease-in-out;
            z-index: -1;
            border-radius: 25px;
          }

          .hero-button:hover::before {
            width: 130%;
          }

          .hero-button:hover {
            color: #fff;
            background-color: transparent;
          }

          .hero-button > span {
            position: relative;
            z-index: 2;
          }
        `}
      </style>

      <section ref={heroRef} style={containerStyle}>
        <div style={contentStyle}>
          <h3 style={animatedSubHeadingStyle}>ART OF FINE DINING</h3>
          <h1 style={animatedHeadingStyle}>
            DINING REDEFINE<br />
            WITH <span style={{ color: '#D6A54F', fontFamily: 'Playfair Display, serif' }}>
              EVERY BITE
            </span>
          </h1>
          <p style={animatedParagraphStyle}>
            Immerse yourself in a dining experience like no other, where every dish is a masterpiece of flavor,
            crafted with care and precision. From the freshest ingredients.
          </p>
          <a href="#contact-us" style={{ textDecoration: 'none' }}>
            <button style={animatedButtonStyle} className="hero-button">
              <span>Book A Table</span>
              <span style={{ fontSize: isMobile ? '14px' : '16px' }}>→</span>
            </button>
          </a>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
